/**
 * Data layer for `detail-drawer-over-row`. Thin typed fetch wrappers over /api/v1/detail-drawer-over-row.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DetailDrawerOverRowRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDetailDrawerOverRowResponse {
	count: number;
	items: DetailDrawerOverRowRow[];
}

export interface CreateDetailDrawerOverRowBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/detail-drawer-over-row";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDetailDrawerOverRowResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDetailDrawerOverRowResponse>(res);
}

export async function create(body: CreateDetailDrawerOverRowBody): Promise<DetailDrawerOverRowRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DetailDrawerOverRowRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const detailDrawerOverRowQueryKeys = {
	all: ["detail-drawer-over-row"] as const,
	list: () => [...detailDrawerOverRowQueryKeys.all] as const,
	detail: (id: string) => [...detailDrawerOverRowQueryKeys.all, id] as const,
};
